// import { createTheme } from '@mui/material/styles';

// export const theme = createTheme({
//   palette: {
//     mode: 'light',
//     primary: {
//       main: '#1a56db',  // Enterprise blue
//       light: '#3b82f6',
//       dark: '#1e40af',
//       contrastText: '#ffffff',
//     },
//     secondary: {
//       main: '#7c3aed',  // Purple for accents
//       light: '#a78bfa',
//       dark: '#5b21b6',
//     },
//     error: {
//       main: '#dc2626',
//       light: '#ef4444',
//       dark: '#b91c1c',
//     },
//     warning: {
//       main: '#f59e0b',
//       light: '#fbbf24',
//       dark: '#d97706',
//     },
//     success: {
//       main: '#10b981',
//       light: '#34d399',
//       dark: '#059669',
//     },
//     info: {
//       main: '#3b82f6',
//       light: '#60a5fa',
//       dark: '#2563eb',
//     },
//     background: {
//       default: '#f8fafc',
//       paper: '#ffffff',
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h1: { fontSize: '2rem', fontWeight: 600 },
//     h2: { fontSize: '1.75rem', fontWeight: 600 },
//     h3: { fontSize: '1.5rem', fontWeight: 600 },
//     h4: { fontSize: '1.25rem', fontWeight: 600 },
//     h5: { fontSize: '1.125rem', fontWeight: 600 },
//     h6: { fontSize: '1rem', fontWeight: 600 },
//     subtitle1: { fontSize: '0.875rem', fontWeight: 500 },
//     body1: { fontSize: '0.875rem' },
//     body2: { fontSize: '0.8125rem' },
//   },
//   shape: {
//     borderRadius: 8,
//   },
//   components: {
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           textTransform: 'none',
//           fontWeight: 500,
//         },
//       },
//     },
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
//           borderRadius: 12,
//         },
//       },
//     },
//     MuiPaper: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//         },
//       },
//     },
//   },
// });

// frontend/src/theme/index.ts
import { createTheme, PaletteMode } from '@mui/material/styles';

// Common colors (work in both themes)
const commonColors = {
  primary: {
    main: '#1a56db',
    light: '#3b82f6',
    dark: '#1e40af',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#7c3aed',
    light: '#a78bfa',
    dark: '#5b21b6',
  },
  error: {
    main: '#dc2626',
    light: '#ef4444',
    dark: '#b91c1c',
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
  },
  success: {
    main: '#10b981',
    light: '#34d399',
    dark: '#059669',
  },
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
  },
};

// Light theme palette
const lightPalette = {
  mode: 'light' as PaletteMode,
  ...commonColors,
  background: {
    default: '#f8fafc',
    paper: '#ffffff',
  },
  text: {
    primary: '#0f172a',
    secondary: '#475569',
    disabled: '#94a3b8',
  },
  divider: '#e2e8f0',
};

// Dark theme palette — pure black base, near-black surfaces
const darkPalette = {
  mode: 'dark' as PaletteMode,
  ...commonColors,
  background: {
    default: '#000000',   // Page background — pure black
    paper: '#0A0A0A',     // Cards, menus, dialogs — near-black surface
  },
  text: {
    primary: '#f8fafc',
    secondary: '#94a3b8',
    disabled: '#64748b',
  },
  divider: '#1F1F1F',
};

// Create theme based on mode
export const getTheme = (mode: PaletteMode) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;

  return createTheme({
    palette,
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2rem', fontWeight: 600 },
      h2: { fontSize: '1.75rem', fontWeight: 600 },
      h3: { fontSize: '1.5rem', fontWeight: 600 },
      h4: { fontSize: '1.25rem', fontWeight: 600 },
      h5: { fontSize: '1.125rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      subtitle1: { fontSize: '0.875rem', fontWeight: 500 },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8125rem' },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'dark'
              ? '0 1px 3px 0 rgb(0 0 0 / 0.6)'
              : '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
            borderRadius: 12,
            backgroundColor: mode === 'dark' ? '#0A0A0A' : '#ffffff',
            border: mode === 'dark' ? '1px solid #1F1F1F' : 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: mode === 'dark' ? '#0A0A0A' : '#ffffff',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#000000' : '#ffffff',
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark' ? '#000000' : '#ffffff',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#1A1A1A' : '#f1f5f9',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${mode === 'dark' ? '#1F1F1F' : '#e2e8f0'}`,
          },
          head: {
            backgroundColor: mode === 'dark' ? '#0A0A0A' : '#f8fafc',
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: mode === 'dark' ? '#1F1F1F' : '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: mode === 'dark' ? '#334155' : '#94a3b8',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1a56db',
              },
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? '#94a3b8' : '#475569',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#0A0A0A' : '#ffffff',
          },
          icon: {
            color: mode === 'dark' ? '#94a3b8' : '#475569',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#1A1A1A' : '#f1f5f9',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#1A1A1A' : '#f1f5f9',
          },
          label: {
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: mode === 'dark' ? '#1F1F1F' : '#e2e8f0',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark' ? '#0A0A0A' : '#ffffff',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? '#1A1A1A' : '#1e293b',
            color: '#ffffff',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#000000' : '#ffffff',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#0A0A0A' : '#f1f5f9',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#94a3b8' : '#475569',
          },
        },
      },
    },
  });
};

// Default export for backward compatibility (if any file still uses this)
export const theme = getTheme('light');